// Components
import { Feature } from '.generated/templates/Feature.Items.model';
import classNames from 'classnames';
import { useEffect, useRef, useState } from 'react';
import { Sitecore } from '.generated/templates/_.Sitecore.Override';
import EditingHelpText from 'components/helpers/EditingHelpText';
import { DangerousHtml } from 'components/helpers/DangerousHtml/DangerousHtml';
import { DEPLOYED_ENVIRONMENT } from 'lib/types/environments';
import { getEnum } from 'lib/get-enum';
import useWindowDimensions from 'components/helpers/WindowsSize/WindowSize';
import { getOffsetPositionOfElement } from 'lib/utils/component-util';
declare global {
  interface Window {
    fsApi: any;
  }
}

export type FormstackProps = Sitecore.Override.ComponentBase &
  Feature.Components.Forms.Fields.Formstack;

export type FormType = 'core' | 'salesforce';

/** Since Forms Core uses document.write which isn't allowed from an async script,
 * we load the form in a hidden iframe, which we then wait for it to load
 * and take the result and render that out.
 */
const RenderFormsCore = ({
  html,
  isV4Form,
}: {
  html: string;
  isV4Form: boolean | undefined;
}): JSX.Element => {
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const [content, setContent] = useState('');
  // Return empty if form script is not mentioned
  if (!html) {
    return <></>;
  }

  const onLoad = () => {
    setContent(iframeRef.current?.contentDocument?.body.innerHTML || '');
    iframeRef.current?.remove();
  };
  return (
    <>
      {isV4Form ? (
        <>
          <DangerousHtml className="formstack-form" html={html} />
        </>
      ) : (
        <>
          <iframe className="hidden" ref={iframeRef} onLoad={onLoad} srcDoc={html}></iframe>
          <DangerousHtml triggerLoadEvent={!!content} className="formstack-form" html={content} />
        </>
      )}
    </>
  );
};

// We can only have 1 form per page.  If more than one present, show error in EE
// IMPORTANT NOTE: As a module level variable, this doesn't get cleared out on soft navigation via next/link
// so we cannot simply not render the form, as this caused forms not to render if they visited a previous page with a form.
let renderedFormUid = '';

const Formstack = ({ fields, rendering }: FormstackProps): JSX.Element => {
  let formScript;
  const isV4Form = fields?.isV4Form?.value;
  let formId = '';
  const { width } = useWindowDimensions();

  switch (DEPLOYED_ENVIRONMENT) {
    case 'development':
      formScript = fields?.developmentFormScript?.value;
      formId = fields?.developmentFormId?.value || '';
      break;
    case 'staging':
      formScript = fields?.stagingFormScript?.value;
      formId = fields?.stagingFormId?.value || '';
      break;
    case 'prod':
      formScript = fields?.productionFormScript?.value;
      formId = fields?.productionFormId?.value || '';
      break;
  }

  const componentDivRef = useRef<HTMLDivElement>(null);

  const onSubmit = async (e: SubmitEvent) => {
    // Prevent normal form submission and do it via AJAX
    e.preventDefault();
    const form = e.target as HTMLFormElement;
    // Proxy to prevent CORS issues
    const url = `/api/rewrite/proxy?url=${form.action}`;

    const response = await fetch(url, {
      method: form.method,
      body: new FormData(form),
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
      },
    });
    if (!response.ok) return;
    let newHtml = '';
    try {
      const responseText = await response.text();
      // It's a redirect
      if (responseText.indexOf('http') === 0) {
        window.location.href = responseText;
      } else {
        const tempDiv = document.createElement('div');

        // Write the contents to a temporary div
        tempDiv.innerHTML = responseText;

        // We only care about this section
        const message = tempDiv.querySelector('.fsPage') ?? tempDiv;

        newHtml = message?.innerHTML;
        //960 is our Mobile layout breakpoint, this adds scroll to submission message
        if (width < 960) {
          scrollTo({
            left: getOffsetPositionOfElement(tempDiv).left,
            //350 is to account for header
            top: getOffsetPositionOfElement(tempDiv).top - 350,
            behavior: 'smooth',
          });
        }
      }
    } catch {
      newHtml = fields?.submissionErrorMessage.value || 'An unexpected error has occurred.';
    }
    if (componentDivRef.current) {
      componentDivRef.current.innerHTML = newHtml;

      //960 is our Mobile layout breakpoint, this adds scroll to submission message
      if (width < 960) {
        scrollTo({
          left: getOffsetPositionOfElement(componentDivRef.current).left,
          //350 is to account for header
          top: getOffsetPositionOfElement(componentDivRef.current).top - 350,
          behavior: 'smooth',
        });
      }
    }
  };

  renderedFormUid = rendering.uid ?? '';

  useEffect(() => {
    // Callback function to execute when mutations are observed
    const callback = (mutationsList: any) => {
      for (const mutation of mutationsList) {
        if (mutation.type === 'childList') {
          // Check if the dynamically loaded element exists
          const formElement = componentDivRef?.current?.querySelector('form');
          if (formElement) {
            //register custom submit event to form to show response message
            formElement.addEventListener('submit', onSubmit);
            if (window.fsApi) {
              const form = window.fsApi().getForm(formId);
              //register formstack form submit event and prevent redirection on submit
              form.registerFormEventListener({
                type: 'submit',
                onFormEvent: (event: { preventDefault: () => void }) => {
                  event.preventDefault();
                  return Promise.resolve(event);
                },
              });
            }
          }
        }
      }
    };

    // Create an observer instance linked to the callback function
    const observer = new MutationObserver(callback);

    // Start observing the target node for configured mutations
    if (componentDivRef?.current) {
      observer.observe(componentDivRef?.current, { childList: true, subtree: true });
    }

    // Cleanup function to disconnect the observer when the component unmounts
    return () => {
      observer.disconnect();
      const formElement = componentDivRef?.current?.querySelector('form');
      if (formElement) {
        formElement?.removeEventListener('submit', onSubmit);
      }
    };
  }, []);

  // Return empty if form script is not added
  if (!formScript) {
    return (
      <EditingHelpText
        condition={true}
        text={`Form script not specified for '${DEPLOYED_ENVIRONMENT}' environment`}
      />
    );
  }

  const formType = getEnum<FormType>(fields?.formType);

  const isFormsCore = formType === 'core';

  const componentAnchorId = rendering?.uid;

  return (
    <div
      ref={componentDivRef}
      className={classNames('w-full anchorIdIndentifier')}
      data-component="authorable/forms/formstack"
      id={componentAnchorId}
    >
      <EditingHelpText
        condition={!!renderedFormUid && renderedFormUid !== rendering.uid}
        text="Only 1 form allowed per page"
      />
      <EditingHelpText condition={true} text="Click here to access form edit options" />

      <EditingHelpText condition={!formType} text="Please select form type" />
      {isFormsCore || !formType ? (
        <RenderFormsCore html={`${formScript}`} isV4Form={isV4Form} />
      ) : (
        <DangerousHtml html={formScript} />
        // <RenderFormsCore html={`${formScript}`} />
      )}
    </div>
  );
};

export default Formstack;
